import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["truncated", "ExternalIconName"],
  _excluded2 = ["children", "disabled", "external", "ExternalIcon", "href", "iconProps", "linkRef", "hrefLang", "onClick", "onKeyDown", "rel", "role", "tabIndex", "target", "to", "truncate", "truncateStringProps", "underline", "use", "prerender", "hideExternalLinkIcon", "onMouseOver", "onMouseOut"];
import unescapedText from 'I18n/utils/unescapedText';
import { memo, useEffect, useRef, useCallback } from 'react';
import { useTheme, isTrellisTheme } from 'foundations-theming/utils';
import styled from 'foundations-theming-specialty/utils';
import devLogger from 'react-utils/devLogger';
import { getRel, getTarget, warnIfShouldBeExternal, renderSecurityTaggedLink } from './Link.utils';
import { getSafeUrl } from '../../utils/unsafeUrl';
import { getTabIndex } from '../../utils/getTabIndex';
import { getColorTokensForUse } from './Link.styles';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { TruncateString } from '../TruncateString';
import { useTagContext } from '../Tag/TagProvider';
import { useTabContext } from '../Tabs/TabProvider';
import { useTabsContext } from '../Tabs/TabsProvider';
import { deepMerge } from '../../utils/deepMerge';
import { useSuppressLinkUnderlineContext } from './SuppressLinkUnderlineContext';
import setSpeculationRulesScript, { prerenderSelectors } from '../../utils/Link/setSpeculationRules';
import { LinkProvider } from './LinkProvider';
import { getComputedLinkUse, getLinkIconStyles } from './Link.helpers';
import { getFocusStyleObject } from '../../utils/focus/focus.style';
import { useControlContext } from './ControlContext';
import { useIsInFilledToolbar } from '../ToolBar/ToolBarContext';
import { TRANSITION_ALL_STYLE } from '../../../constants/transition-all';
import { ExternalLinkIcon } from 'foundations-assets/icons/components/ExternalLinkIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const StyledIconWrapper = styled.span.withConfig({
  displayName: "Link__StyledIconWrapper",
  componentId: "sc-197oqnz-0"
})(({
  theme
}) => {
  return {
    marginInline: theme.space['100'],
    display: 'inline-block'
  };
});
const StyledExternalIcon = props => {
  const {
      ExternalIconName
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const IconName = ExternalIconName || ExternalLinkIcon;
  return /*#__PURE__*/_jsx(StyledIconWrapper, {
    children: /*#__PURE__*/_jsx(IconName, Object.assign({
      alignWithText: true
    }, rest))
  });
};
export const StyledLink = styled.a.withConfig({
  displayName: "Link__StyledLink",
  componentId: "sc-197oqnz-1"
})(({
  theme,
  $alwaysUnderline,
  $cssStyles,
  $disabled,
  $use,
  $styleOverrides,
  $suppressUnderline,
  $hasIconStyleOverrides,
  $isControl
}) => {
  const colorTokensForUse = getColorTokensForUse($use, $isControl);
  const unstyled = $use === 'unstyled';
  return deepMerge(Object.assign({
    // The border radius is always applied so that there aren't any border radius changes when the focus ring appears
    borderRadius: theme.borderRadius['100']
  }, getFocusStyleObject({
    theme,
    hasTransitionAll: true
  }), $use === 'on-fill' && {
    '& svg[data-icon-name]': {
      fill: `${theme.color[colorTokensForUse.idle]}!important`
    },
    '&:hover svg[data-icon-name]': {
      fill: `${theme.color[colorTokensForUse.hover]}!important`
    }
  }, !$hasIconStyleOverrides && {
    '& svg[data-icon-name]': {
      transition: TRANSITION_ALL_STYLE,
      fill: theme.color[getColorTokensForUse($use).idle]
    },
    '&:hover svg[data-icon-name]': {
      fill: theme.color[getColorTokensForUse($use).hover]
    },
    '&:active svg[data-icon-name]': {
      fill: theme.color[getColorTokensForUse($use).active]
    },
    '&:disabled svg[data-icon-name]': {
      fill: theme.color[getColorTokensForUse($use).disabled]
    }
  }, {
    '& > svg[data-icon-name]': {
      verticalAlign: '-0.1em',
      color: theme.color[colorTokensForUse.idle]
    },
    fontWeight: !unstyled ? theme.typography['heading-25'].fontWeight : theme.typography['body-100'].fontWeight,
    transition: TRANSITION_ALL_STYLE
  }, $disabled ? {
    color: theme.color[colorTokensForUse.disabled],
    cursor: 'not-allowed',
    '&:hover': {
      color: theme.color[colorTokensForUse.disabled],
      cursor: 'not-allowed',
      textDecoration: 'none'
    }
  } : {
    color: theme.color[colorTokensForUse.idle],
    cursor: 'pointer',
    textDecoration: $alwaysUnderline && !unstyled ? 'underline solid' : 'inherit',
    '&:hover': {
      color: theme.color[colorTokensForUse.hover],
      textDecoration: unstyled || $suppressUnderline ? 'inherit' : 'underline solid'
    },
    '&:active': {
      color: theme.color[colorTokensForUse.active],
      textDecoration: unstyled || $suppressUnderline ? 'inherit' : 'underline solid'
    }
  }), $styleOverrides, $cssStyles);
});
export const MemolessLink = _ref => {
  var _tagContext$linkStyle, _tabContext$tabLinkSt, _tabsContext$tabsLink;
  let {
      children,
      disabled = false,
      external = false,
      ExternalIcon,
      href,
      iconProps,
      linkRef,
      hrefLang,
      onClick,
      onKeyDown,
      rel,
      role,
      tabIndex,
      target,
      // @ts-expect-error - Property 'to' does not exist on type 'LinkProps & { children?: ReactNode; }'
      to,
      truncate = false,
      truncateStringProps,
      underline = false,
      use = 'dark',
      prerender,
      hideExternalLinkIcon = false,
      onMouseOver,
      onMouseOut
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded2);
  if (process.env.NODE_ENV !== 'production') {
    if (to != null) {
      devLogger.warn({
        message: `Link: The \`to\` prop is a no-op. Did you mean \`href\`? (\`to="${to}"\`)`,
        key: 'Link: to'
      });
    }
  }
  setSpeculationRulesScript();
  const defaultLinkRef = useRef(null);
  const {
    domProps,
    tokenProps
  } = filterProps(rest, {
    isLink: true
  });
  const theme = useTheme();
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const tagContext = useTagContext();
  const tabContext = useTabContext();
  const tabsContext = useTabsContext();
  const {
    isControl
  } = useControlContext() || {};
  const isTrellis = isTrellisTheme(theme);
  const {
    suppressUnderline: shouldSuppressUnderline
  } = useSuppressLinkUnderlineContext();
  const linkStyleOverrides = deepMerge((_tagContext$linkStyle = tagContext === null || tagContext === void 0 ? void 0 : tagContext.linkStyleOverrides) !== null && _tagContext$linkStyle !== void 0 ? _tagContext$linkStyle : {}, (_tabContext$tabLinkSt = tabContext === null || tabContext === void 0 ? void 0 : tabContext.tabLinkStyleOverrides) !== null && _tabContext$tabLinkSt !== void 0 ? _tabContext$tabLinkSt : {}, (_tabsContext$tabsLink = tabsContext === null || tabsContext === void 0 ? void 0 : tabsContext.tabsLinkStyleOverrides) !== null && _tabsContext$tabsLink !== void 0 ? _tabsContext$tabsLink : {});
  const isInFilledToolbar = useIsInFilledToolbar();
  const computedUse = getComputedLinkUse({
    use,
    isInFilledToolbar
  });
  // Calculate icon styles for the context
  const iconStyleOverrides = getLinkIconStyles(computedUse, disabled, theme);

  /*
   * Adds the `ping` attribute to the `a` element when the `ping` prop changes.
   * This fixes the bug reported in https://git.hubteam.com/HubSpot/foundations-components/issues/677
   */
  useEffect(() => {
    if (domProps.ping !== undefined) {
      var _defaultLinkRef$curre;
      defaultLinkRef === null || defaultLinkRef === void 0 || (_defaultLinkRef$curre = defaultLinkRef.current) === null || _defaultLinkRef$curre === void 0 || _defaultLinkRef$curre.setAttribute('ping', domProps.ping);
    }
  }, [domProps.ping]);
  const handleClick = evt => {
    if (disabled) {
      evt.preventDefault();
      evt.stopPropagation();
    } else {
      if (onClick) onClick(evt);
    }
  };
  const handleKeyDown = evt => {
    // Simulate click on Spacebar, to emulate button behavior
    const {
      key
    } = evt;
    if (onKeyDown) onKeyDown(evt);
    if (!onClick) return;

    // Ignore keydown events that bubbled up
    if (evt.target !== (linkRef || defaultLinkRef).current) return;
    if (key === ' ') {
      if (disabled) {
        evt.preventDefault();
        evt.stopPropagation();
        return;
      }
      // @ts-expect-error - KeyboardEvent is not the same as MouseEvent
      onClick(evt);

      // Prevent scrolling
      evt.preventDefault();
    }
  };
  const getRole = useCallback(() => {
    if (href) {
      if (disabled) {
        /**
         * When disabled=true, href will be suppressed and consequently link will falllback to role="generic"
         * @see https://www.w3.org/TR/html-aria/#example-communicate-a-disabled-link-with-aria
         */
        return 'link';
      }
      /**
       * When disabled=false, link will use implicit role="link" ARIA semantics
       * @see https://www.w3.org/TR/html-aria/#docconformance:~:text=Rules%20of%20ARIA%20attribute%20usage%20by%20HTML%20element
       */
      return undefined;
    }
    return role || 'button';
  }, [disabled, href, role]);
  const computedTarget = getTarget(external, target);
  const computedRel = getRel(rel, computedTarget);
  const computedRole = getRole();
  const externalLinkIcon = external && !hideExternalLinkIcon ? /*#__PURE__*/_jsx(StyledExternalIcon, Object.assign({
    "aria-label": unescapedText('salesUI.UILink.externalTitle'),
    ExternalIconName: ExternalIcon
  }, iconProps)) : null;
  if (process.env.NODE_ENV !== 'production') {
    warnIfShouldBeExternal(external, computedTarget, 'Link');
  }
  const childrenWithProvider = /*#__PURE__*/_jsx(LinkProvider, {
    use: computedUse,
    disabled: disabled,
    iconStyleOverrides: iconStyleOverrides,
    children: children
  });
  const wrappedChildren = truncate ? /*#__PURE__*/_jsx(TruncateString, Object.assign({
    useFlex: true,
    fixedChildren: externalLinkIcon
  }, truncateStringProps, {
    children: childrenWithProvider
  })) : childrenWithProvider;
  const renderedLink = /*#__PURE__*/_jsxs(StyledLink, Object.assign({
    onClick: handleClick,
    onKeyDown: handleKeyDown
  }, domProps, {
    "aria-selected": rest['aria-selected'],
    "aria-current": rest['aria-current'],
    $alwaysUnderline: !isControl && !shouldSuppressUnderline && (underline || ['on-bright', 'on-dark'].includes(computedUse) || isTrellis),
    "data-link-use": computedUse,
    $use: computedUse,
    $disabled: disabled,
    $cssStyles: cssStyles,
    $styleOverrides: linkStyleOverrides !== null && linkStyleOverrides !== void 0 ? linkStyleOverrides : {},
    $hasIconStyleOverrides: !!iconStyleOverrides,
    $truncate: truncate,
    $isControl: isControl,
    $suppressUnderline: shouldSuppressUnderline || Boolean(isControl),
    href: disabled ? undefined : getSafeUrl(href),
    ref: linkRef || defaultLinkRef,
    rel: computedRel,
    role: computedRole,
    tabIndex: getTabIndex(disabled, tabIndex),
    target: computedTarget,
    "aria-disabled": disabled,
    "data-link": true,
    hrefLang: hrefLang,
    onMouseOver: onMouseOver,
    onMouseOut: onMouseOut
  }, prerender && {
    'data-prerender': prerenderSelectors[prerender]
  }, {
    children: [wrappedChildren, truncate ? null : externalLinkIcon]
  }));
  return renderSecurityTaggedLink(href, renderedLink);
};
export const Link = /*#__PURE__*/memo(MemolessLink);
Link.displayName = 'Link';